import { cn } from "@istari-frontend/shared";
import { Checkbox, Label } from "@istari-frontend/ui";
import type { ComponentProps, ReactNode } from "react";
import { useId } from "react";

// Amber-tinted container for SCS "I confirm" gates that precede an irreversible
// or partner-visible action (creating a sending connection, sharing a file,
// changing an object store, etc.). Compose with one or more
// <CertificationCheckbox>; preamble content (headings, partner lists) sits as
// regular children alongside.
export function CertificationBox({ className, children, ...rest }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/30",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

// Checkbox + label row sized for use inside <CertificationBox>. Forces a
// foreground-colored border so the control reads against the amber background,
// which the default muted border washes out at low contrast.
export function CertificationCheckbox({
  checked,
  onCheckedChange,
  children,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  const checkboxId = useId();
  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id={checkboxId}
        checked={checked}
        onCheckedChange={(val) => onCheckedChange(val === true)}
        className="border-foreground mt-0.5"
      />
      <Label htmlFor={checkboxId} className="block cursor-pointer text-sm leading-snug font-normal">
        {children}
      </Label>
    </div>
  );
}
